import React, { useRef } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { ContactShadows, Environment, Float, Html, RoundedBox } from "@react-three/drei";
import { Banana } from "lucide-react";
import * as THREE from "three";
import { cn } from "@/lib/utils";
import { MadsCssAvatar } from "./MadsCssAvatar";

export type AgentRobotVariant = "mads" | "banan" | "google";

export const VARIANT_CONFIG: Record<
  AgentRobotVariant,
  {
    emissive: string;
    secondaryEmissive: string;
    visorGlowClass: string;
    scanClass: string;
    browColor: string;
    shellColor: string;
  }
> = {
  mads: {
    emissive: "#0082FB",
    secondaryEmissive: "#22d3ee",
    visorGlowClass: "bg-blue-500/20",
    scanClass: "bg-cyan-300/60 shadow-[0_0_8px_rgba(34,211,238,0.8)]",
    browColor: "#334155",
    shellColor: "#1f1f23",
  },
  banan: {
    emissive: "#eab308",
    secondaryEmissive: "#facc15",
    visorGlowClass: "bg-yellow-500/20",
    scanClass: "bg-[#eab308]/60 shadow-[0_0_8px_rgba(234,179,8,0.8)]",
    browColor: "#57534e",
    shellColor: "#231f1a",
  },
  google: {
    emissive: "#4285F4",
    secondaryEmissive: "#34A853",
    visorGlowClass: "bg-blue-500/20",
    scanClass: "bg-[#4285F4]/60 shadow-[0_0_8px_rgba(66,133,244,0.8)]",
    browColor: "#334155",
    shellColor: "#1f1f23",
  },
};

function MetaLogo() {
  return (
    <img
      src="/meta-color.svg"
      alt=""
      aria-hidden="true"
      className="relative z-10 h-full w-full"
      style={{
        filter:
          "drop-shadow(0 0 10px rgba(34,211,238,1)) drop-shadow(0 0 20px rgba(59,130,246,0.8))",
      }}
    />
  );
}

function GoogleLogo() {
  return (
    <svg
      viewBox="0 0 24 24"
      className="relative z-10 h-auto w-[76%]"
      style={{ filter: "drop-shadow(0 0 8px rgba(66,133,244,0.6))" }}
      xmlns="http://www.w3.org/2000/svg"
    >
      <title>Google Ads</title>
      <path
        d="M21.6 12.227c0-.709-.064-1.39-.182-2.045H12v3.868h5.382a4.6 4.6 0 0 1-1.995 3.018v2.51h3.232c1.891-1.742 2.981-4.305 2.981-7.351z"
        fill="#4285F4"
      />
      <path
        d="M12 22c2.7 0 4.964-.895 6.619-2.422l-3.232-2.51c-.895.6-2.04.955-3.387.955-2.605 0-4.81-1.76-5.595-4.123H3.064v2.59A9.996 9.996 0 0 0 12 22z"
        fill="#34A853"
      />
      <path
        d="M6.405 13.9A6.013 6.013 0 0 1 6.09 12c0-.659.114-1.3.314-1.9V7.51H3.064A9.996 9.996 0 0 0 2 12c0 1.614.386 3.14 1.064 4.49l3.341-2.59z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.977c1.468 0 2.787.505 3.823 1.495l2.868-2.868C16.96 2.99 14.696 2 12 2 8.09 2 4.708 4.245 3.064 7.51l3.34 2.59C7.19 7.736 9.396 5.977 12 5.977z"
        fill="#EA4335"
      />
    </svg>
  );
}

/**
 * Procedural 3D robot head shared by the marketing hero and single-instance
 * agent avatars. The visor is projected as HTML so icon details stay crisp.
 *
 * Rendering many canvases would exhaust WebGL contexts, so list surfaces keep
 * the CSS avatars and large/single-instance surfaces opt into `render3d`.
 */
export function RobotHead({
  variant,
  position = [0, 0, 0],
  baseRotation = [0, 0, 0],
  scale = 1,
}: {
  variant: AgentRobotVariant;
  position?: [number, number, number];
  baseRotation?: [number, number, number];
  scale?: number;
}) {
  const group = useRef<THREE.Group>(null);
  const cfg = VARIANT_CONFIG[variant];

  useFrame((state, delta) => {
    if (!group.current) return;

    const targetX = baseRotation[0] + (state.pointer.y * Math.PI) / 7;
    const targetY = baseRotation[1] + (state.pointer.x * Math.PI) / 7;

    group.current.rotation.x = THREE.MathUtils.damp(group.current.rotation.x, targetX, 4, delta);
    group.current.rotation.y = THREE.MathUtils.damp(group.current.rotation.y, targetY, 4, delta);
    group.current.rotation.z = THREE.MathUtils.damp(
      group.current.rotation.z,
      baseRotation[2],
      4,
      delta,
    );
  });

  return (
    <Float speed={2} rotationIntensity={0.25} floatIntensity={1} floatingRange={[-0.08, 0.08]}>
      <group ref={group} position={position} rotation={baseRotation} scale={scale}>
        <mesh position={[0, -1.25, -0.1]}>
          <cylinderGeometry args={[0.32, 0.4, 0.5, 32]} />
          <meshStandardMaterial color="#1a1a1c" metalness={0.9} roughness={0.3} />
        </mesh>

        <mesh position={[0, -1.1, -0.1]} rotation={[Math.PI / 2, 0, 0]}>
          <torusGeometry args={[0.42, 0.08, 16, 32]} />
          <meshStandardMaterial color="#3f3f46" metalness={0.8} roughness={0.2} />
        </mesh>

        <RoundedBox args={[1.9, 2.3, 1.9]} radius={0.25} smoothness={4}>
          <meshStandardMaterial color={cfg.shellColor} metalness={0.85} roughness={0.2} />
        </RoundedBox>

        <RoundedBox args={[1.82, 0.08, 1.82]} radius={0.04} position={[0, 1.15, 0]}>
          <meshStandardMaterial color={cfg.browColor} metalness={0.9} roughness={0.15} />
        </RoundedBox>

        <RoundedBox args={[1.72, 1.05, 1.95]} radius={0.08} position={[0, 0.15, 0.05]}>
          <meshStandardMaterial color="#050505" metalness={0.95} roughness={0.08} />
        </RoundedBox>

        <RoundedBox args={[1.95, 0.45, 1.95]} radius={0.08} position={[0, -0.95, 0]}>
          <meshStandardMaterial color="#121214" metalness={0.9} roughness={0.3} />
        </RoundedBox>

        {variant === "mads" || variant === "google" ? (
          <>
            <RoundedBox args={[0.08, 0.85, 0.08]} radius={0.02} position={[-0.74, 0.18, 1.01]}>
              <meshStandardMaterial
                color={cfg.secondaryEmissive}
                emissive={cfg.secondaryEmissive}
                emissiveIntensity={1.2}
                toneMapped={false}
              />
            </RoundedBox>
            <RoundedBox args={[0.08, 0.85, 0.08]} radius={0.02} position={[0.74, 0.18, 1.01]}>
              <meshStandardMaterial
                color={cfg.secondaryEmissive}
                emissive={cfg.secondaryEmissive}
                emissiveIntensity={1.2}
                toneMapped={false}
              />
            </RoundedBox>
            <mesh position={[0, 1.02, 0.98]} rotation={[Math.PI / 2, 0, 0]}>
              <torusGeometry args={[0.46, 0.018, 12, 42, Math.PI * 1.55]} />
              <meshStandardMaterial
                color={cfg.emissive}
                emissive={cfg.emissive}
                emissiveIntensity={1.35}
                toneMapped={false}
              />
            </mesh>
          </>
        ) : (
          <>
            <RoundedBox
              args={[0.48, 0.12, 0.12]}
              radius={0.06}
              position={[-0.48, 1.23, 0.74]}
              rotation={[0, 0, 0.22]}
            >
              <meshStandardMaterial
                color={cfg.emissive}
                emissive={cfg.emissive}
                emissiveIntensity={0.7}
                metalness={0.55}
                roughness={0.22}
                toneMapped={false}
              />
            </RoundedBox>
            <RoundedBox
              args={[0.48, 0.12, 0.12]}
              radius={0.06}
              position={[0.48, 1.23, 0.74]}
              rotation={[0, 0, -0.22]}
            >
              <meshStandardMaterial
                color={cfg.emissive}
                emissive={cfg.emissive}
                emissiveIntensity={0.7}
                metalness={0.55}
                roughness={0.22}
                toneMapped={false}
              />
            </RoundedBox>
            <mesh position={[0, -1.08, 1.02]} rotation={[Math.PI / 2, 0, 0]}>
              <torusGeometry args={[0.34, 0.025, 10, 36, Math.PI]} />
              <meshStandardMaterial
                color={cfg.secondaryEmissive}
                emissive={cfg.secondaryEmissive}
                emissiveIntensity={0.85}
                toneMapped={false}
              />
            </mesh>
          </>
        )}

        <RoundedBox args={[0.9, 0.08, 0.1]} radius={0.02} position={[0, -0.95, 1]}>
          <meshStandardMaterial color="#000000" roughness={0.5} />
        </RoundedBox>
        <RoundedBox args={[0.7, 0.03, 0.1]} radius={0.01} position={[0, -1.02, 1]}>
          <meshStandardMaterial color="#3f3f46" metalness={0.5} />
        </RoundedBox>

        <mesh position={[-1, 0.1, 0]} rotation={[0, 0, Math.PI / 2]}>
          <cylinderGeometry args={[0.28, 0.28, 0.15, 32]} />
          <meshStandardMaterial color="#3a3a3e" metalness={0.7} roughness={0.3} />
        </mesh>
        <mesh position={[-1.05, 0.1, 0]} rotation={[0, 0, Math.PI / 2]}>
          <cylinderGeometry args={[0.14, 0.14, 0.16, 32]} />
          <meshStandardMaterial
            color={cfg.emissive}
            emissive={cfg.emissive}
            emissiveIntensity={1.5}
            toneMapped={false}
          />
        </mesh>

        <mesh position={[1, 0.1, 0]} rotation={[0, 0, Math.PI / 2]}>
          <cylinderGeometry args={[0.28, 0.28, 0.15, 32]} />
          <meshStandardMaterial color="#3a3a3e" metalness={0.7} roughness={0.3} />
        </mesh>
        <mesh position={[1.05, 0.1, 0]} rotation={[0, 0, Math.PI / 2]}>
          <cylinderGeometry args={[0.14, 0.14, 0.16, 32]} />
          <meshStandardMaterial
            color={cfg.emissive}
            emissive={cfg.emissive}
            emissiveIntensity={1.5}
            toneMapped={false}
          />
        </mesh>

        <mesh position={[0, 1.5, 0]}>
          <cylinderGeometry args={[0.05, 0.06, 0.5, 16]} />
          <meshStandardMaterial color="#52525b" metalness={0.8} roughness={0.4} />
        </mesh>
        <mesh position={[0, 1.85, 0]}>
          <sphereGeometry args={[0.12]} />
          <meshStandardMaterial
            color={cfg.emissive}
            emissive={cfg.emissive}
            emissiveIntensity={2.5}
            toneMapped={false}
          />
        </mesh>

        <Html
          transform
          distanceFactor={2.4}
          position={[0, 0.15, 1]}
          zIndexRange={[100, 0]}
          occlude="blending"
        >
          <div className="relative flex h-[80px] w-[120px] select-none items-center justify-center overflow-hidden rounded-lg pointer-events-none">
            <div
              className={cn(
                "absolute inset-x-0 top-[30%] h-[40%] rounded-lg blur-md",
                cfg.visorGlowClass,
              )}
            />
            <div className="absolute inset-x-1 top-[35%] h-[30%] rounded-lg bg-black/60 blur-[2px]" />

            {variant === "mads" ? (
              <div className="relative z-10 flex h-[85%] w-[85%] animate-pulse items-center justify-center opacity-90">
                <MetaLogo />
              </div>
            ) : variant === "google" ? (
              <div className="relative z-10 flex h-[85%] w-[85%] animate-pulse items-center justify-center opacity-90">
                <GoogleLogo />
              </div>
            ) : (
              <div
                className="relative z-10 flex h-[80%] w-[80%] scale-[1.3] animate-pulse items-center justify-between px-2 text-[#eab308] opacity-90"
                style={{
                  filter:
                    "drop-shadow(0 0 10px rgba(234,179,8,1)) drop-shadow(0 0 20px rgba(234,179,8,0.8))",
                }}
              >
                <Banana className="h-auto w-[40%] scale-x-[-1] transform" strokeWidth={2} />
                <Banana className="h-auto w-[40%]" strokeWidth={2} />
              </div>
            )}

            <div
              className={cn("absolute inset-x-0 h-[2px] blur-[1px]", cfg.scanClass)}
              style={{ animation: "agent-avatar-scan 2.5s linear infinite" }}
            />
            <style>{`
              @keyframes agent-avatar-scan {
                0% { top: 15%; }
                50% { top: 85%; }
                100% { top: 15%; }
              }
            `}</style>
          </div>
        </Html>
      </group>
    </Float>
  );
}

export function RobotAvatarCanvas({
  variant,
  className,
  style,
}: {
  variant: AgentRobotVariant;
  className?: string;
  style?: React.CSSProperties;
}) {
  const cfg = VARIANT_CONFIG[variant];

  return (
    <div className={cn("relative overflow-hidden", className)} style={style}>
      <Canvas camera={{ position: [0, 0.15, 5], fov: 42 }} dpr={[1, 2]} gl={{ alpha: true }}>
        <ambientLight intensity={0.5} />
        <spotLight position={[5, 8, 5]} angle={0.25} penumbra={1} intensity={1.4} />
        <directionalLight position={[-4, 4, -2]} intensity={0.4} />
        <pointLight position={[0, -2, 2]} intensity={0.5} color={cfg.emissive} />
        <Environment preset="city" />
        <RobotHead variant={variant} />
      </Canvas>
    </div>
  );
}

export function AgentRobotPairCanvas({
  className,
  style,
}: {
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <div className={cn("relative overflow-hidden", className)} style={style}>
      <Canvas camera={{ position: [0, 0.1, 6.4], fov: 42 }} dpr={[1, 2]} gl={{ alpha: true }}>
        <ambientLight intensity={0.55} />
        <spotLight position={[5, 7, 6]} angle={0.28} penumbra={0.9} intensity={1.35} />
        <spotLight
          position={[-5, 5, 4]}
          angle={0.36}
          penumbra={1}
          intensity={0.75}
          color="#60a5fa"
        />
        <pointLight
          position={[2, -1.4, 2.4]}
          intensity={0.55}
          color={VARIANT_CONFIG.banan.emissive}
        />
        <pointLight
          position={[-2, -1.4, 2.4]}
          intensity={0.55}
          color={VARIANT_CONFIG.mads.secondaryEmissive}
        />
        <Environment preset="city" />
        <RobotHead
          variant="mads"
          position={[-1.45, 0.04, 0]}
          baseRotation={[0.03, 0.18, -0.02]}
          scale={1.08}
        />
        <RobotHead
          variant="banan"
          position={[1.45, -0.02, 0]}
          baseRotation={[0.02, -0.18, 0.02]}
          scale={1.08}
        />
        <ContactShadows position={[0, -2.5, 0]} opacity={0.38} scale={8} blur={2.3} far={4} />
      </Canvas>
    </div>
  );
}

export function AlienAvatar({
  className,
  style,
  render3d,
}: {
  className?: string;
  style?: React.CSSProperties;
  render3d?: boolean;
}) {
  if (render3d) {
    return <RobotAvatarCanvas variant="mads" className={className} style={style} />;
  }

  return <MadsCssAvatar className={className} style={style} />;
}
